import React, { useState, useMemo } from 'react';
import TradingViewChartIcon from './TradingViewChartIcon';
import './CrossCombinationView.css';
import { ensureArray } from './config';
import { useWorkspace } from './workspace/WorkspaceContext';

const formatTradingViewSymbol = (symbol, workspace) => {
  const cleaned = symbol.replace(/\s*(_UMCBL)$/, "");
  const suffix = workspace.marketType === 'PERPS' ? '.P' : '';
  return `${workspace.exchange}:${cleaned}${suffix}`;
};

const copyToClipboard = (text) => {
  // ✅ Safe check for secure context and clipboard support
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text)
      .then(() => alert("Copied to clipboard!"))
      .catch((err) => {
        console.error("Clipboard API failed:", err);
        fallbackCopy(text);
      });
  } else {
    fallbackCopy(text);
  }
};

// ✅ Replace your current helpers with these:
const norm = (v) => (typeof v === 'string' ? v.toUpperCase() : '');

const isBullOrBearCross = (val) => {
  const nv = norm(val);
  return nv.includes('BULL') || nv.includes('BEAR');
};

const isActiveCrossValue = (val, token = '') => {
  if (val === true || norm(val) === 'TRUE') return !token;
  return isBullOrBearCross(val) && includesToken(val, token);
};

const includesToken = (v, token) => {
  const nv = norm(v);
  const tk = norm(token);
  return !tk || nv.includes(tk);
};

const getCrossClass = (v) => {
  const nv = norm(v);
  if (nv.includes('BULL')) return 'bull';
  if (nv.includes('BEAR')) return 'bear';
  return '';
};



const fallbackCopy = (text) => {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed"; // Prevent scroll jump
  textArea.style.top = "-9999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    alert(successful ? "Copied to clipboard!" : "Copy failed. Try manually.");
  } catch (err) {
    console.error("Fallback copy failed:", err);
    alert("Copy not supported in this environment.");
  }

  document.body.removeChild(textArea);
};


const crossFields = [
  'volumeBreakout',
  'ema200TrendAlignedEma50BandCross',
  'appPriceXEma50BandCross',
  'priceInsideEma50Band',
  'ema50BandChoppyRangeBreakOut',
  'ema50BandChoppyRangeBreakOutTrendAligned',
  'priceInsideEma200Band',
  'ema200BandCross',
  'ema200BandCrossTrendAligned',
  'firstEma50BandCrossAfterTrendShift',
  'firstEma50BandCrossInLast50Candles',
  'firstEma50BandCrossInLast100Candles',
  'firstEma50BandCrossInLast200Candles',
  'firstEma200BandCrossAfterTrendShift',
  'firstEma200BandCrossInLast50Candles',
  'firstEma200BandCrossInLast100Candles',
  'firstEma200BandCrossInLast200Candles',
  'firstTrendAlignedStochRsiResetAfterTrendShift',
  'firstTrendAlignedStochRsiResetAtEma50BandAfterTrendShift',
  'firstTrendAlignedStochRsiResetAtEma200BandAfterTrendShift',
  'singleGodCandleCrossedAllEmaLevels',
  'singleCandleCrossedLowerEmaStack',
  'priceBeyondAllEmaStackAndEma200Cross',
  'priceXAllEmaStackEdgeCross',
  'priceXLowerEmaStackEdgeCross',
  'priceAboveOrBelowAllEmaLevels',
  'allEmaLevelsInOneTrend',
  'ictMSB',
  'ictSwingHighLowRetest',
  'ictSwingHighLowWickRetest',
  'ictSupplyDemandReversal',
  'ictBreakerReversal',
  'candleTrendReversal',

  'ictManualTradeEntryExitSrCross',
  'appMtfEmaConfirmedCross',
  'appPriceXPosCloseSrConfirmedCross',
  'appPriceXSrConfirmedCross',
  'appPriceXZoneConfirmedCross',
  'priceXSrConfirmedCross',
  'dualFlexiEmaEntryExit',
  'trendBasedStochRsiEntrySignal',
  'trendBasedExtremeStochRsiEntrySignal',
  'ignoreEmaSignalRangeSrCross',
  'szDzZoneBasedStochRsiEntrySignal',
  'appRsiDivEntrySignal',
  'appAoDivEntrySignal',
  'appTouchCloseSrCrossSignal',
  'fvgTagged',
  'appPriceXEma50Cross',
  'appPriceXEma100Cross',
  'appPriceXEma200Cross',
  'appEma50XEma200Cross',
  'mtfPriceXEma100Ema200Cross',
  'mtfPriceXEma100Ema200TrendOnlyCross',
  'ema12ema21SwingHighCloseSrCrossSignal',
  'ema12ema21SwingLowCloseSrCrossSignal',
  'ema12ema21SwingHighWickSrCrossSignal',
  'ema12ema21SwingLowWickSrCrossSignal',
  'ema12ema21SwingLevelStochRsiEntrySignal',
  'appPriceXEma12Cross',
  'appPriceXEma12Ema21Cross',
  'appEma12XEma21Cross',
  'appPriceXEma21Cross',
  'appPriceXEma36Cross',
  'mtfPriceXEma21Ema36Cross',
  'appEma21XEma36Cross',
  'mtfPriceXEma21Ema36TrendOnlyCross',
  'appEma21XEma36CrossEntrySRCrossSignal',
  'swingLevelStochRsiEntrySignal',
  'appPriceXVwap24Cross',
  'appPriceXVwapWeeklyCross',
  'priceXEma36Rejection',
  'candlePattern',
  'priceXSRTrendLineCross',
  'priceXChannelLineCross',
  'priceXSupplyDemandZoneCross',
  'priceXVWAP24Cross',
  'priceXVWAPWeeklyCross',  
  'priceXEma12Cross',
  'priceXEma36Cross',
  'ema12XEma36Cross',  
];

const getSectionTitle = (field) => ({
  volumeBreakout: 'Volume Breakout',
  ema200TrendAlignedEma50BandCross: 'Ema50 Band Cross [EMA200 Trend Aligned]',
  appPriceXEma50BandCross: 'Ema50 Band Cross Signal',
  priceInsideEma50Band: 'Price Inside Ema50 Band',
  ema50BandChoppyRangeBreakOut: 'Ema50 Band Choppy Range BreakOut',
  ema50BandChoppyRangeBreakOutTrendAligned: 'Ema50 Band Choppy Range BreakOut Trend Aligned',
  priceInsideEma200Band: 'Price Inside Ema200 Band',
  ema200BandCross: 'Ema200 Band Cross',
  ema200BandCrossTrendAligned: 'Ema200 Band Cross Trend Aligned',
  firstEma50BandCrossAfterTrendShift: 'First Ema50 Band Cross After Trend Shift',
  firstEma50BandCrossInLast50Candles: 'First Ema50 Band Cross In Last 50 Candles',
  firstEma50BandCrossInLast100Candles: 'First Ema50 Band Cross In Last 100 Candles',
  firstEma50BandCrossInLast200Candles: 'First Ema50 Band Cross In Last 200 Candles',
  firstEma200BandCrossAfterTrendShift: 'First Ema200 Band Cross After Trend Shift',
  firstEma200BandCrossInLast50Candles: 'First Ema200 Band Cross In Last 50 Candles',
  firstEma200BandCrossInLast100Candles: 'First Ema200 Band Cross In Last 100 Candles',
  firstEma200BandCrossInLast200Candles: 'First Ema200 Band Cross In Last 200 Candles',
  firstTrendAlignedStochRsiResetAfterTrendShift: 'First Trend Aligned Stoch RSI Reset After Trend Shift',
  firstTrendAlignedStochRsiResetAtEma50BandAfterTrendShift: 'First Stoch RSI Reset At Ema50 Band After Trend Shift',
  firstTrendAlignedStochRsiResetAtEma200BandAfterTrendShift: 'First Stoch RSI Reset At Ema200 Band After Trend Shift',
  singleGodCandleCrossedAllEmaLevels: 'Single God Candle Crossed All Ema Levels',
  singleCandleCrossedLowerEmaStack: 'Single Candle Crossed Lower Ema Stack',
  priceBeyondAllEmaStackAndEma200Cross: 'Price Beyond All Ema Stack And Ema200 Cross',
  priceXAllEmaStackEdgeCross:  'Price X All Ema Stack Edge Cross',
  priceXLowerEmaStackEdgeCross: 'Price X Lower Ema Stack Edge Cross',
  priceAboveOrBelowAllEmaLevels :'Price Above Or Below All Ema Levels',
  allEmaLevelsInOneTrend: 'All Ema Levels In One Trend',

  ictMSB: 'ICT MSB',
  ictSwingHighLowRetest: 'ICT Swing HighLow Retest',
  ictSwingHighLowWickRetest: 'ICT Swing HighLow Wick Retest',
  ictSupplyDemandReversal: 'ICT Supply Demand Reversal',
  ictBreakerReversal: 'ICT Breaker Reversal',
  candleTrendReversal: 'Candle Trend Reversal',

  ictManualTradeEntryExitSrCross: 'ICT Manual Trade Entry Exit SR Cross',
  appMtfEmaConfirmedCross: 'App Mtf Ema Confirmed Cross',
  appPriceXPosCloseSrConfirmedCross: 'App Price X Position Close Sr Confirmed Cross',
  appPriceXSrConfirmedCross: 'App Price X Sr Confirmed Cross',
  appPriceXZoneConfirmedCross: 'App Price X Sr ConfirmedCross',
  priceXSrConfirmedCross: 'Price X S/R Confirmed Cross',
  dualFlexiEmaEntryExit : 'Dual Flexi Ema Entry Exit',
  trendBasedStochRsiEntrySignal: 'Trend Based StochRsi Entry Signal',
  trendBasedExtremeStochRsiEntrySignal: 'Trend Based Extreme StochRsi Entry Signal',
  ignoreEmaSignalRangeSrCross: 'Ignore Ema Signal Range Sr Cross',
  szDzZoneBasedStochRsiEntrySignal: 'SzDz Zone Based StochRsi Entry Signal',
  appRsiDivEntrySignal: 'App Rsi Div Entry Signal',
  appAoDivEntrySignal: 'App Ao Div Entry Signal',
  appTouchCloseSrCrossSignal: 'App Touch Close Sr Cross Signal',
  fvgTagged: 'FVG Tagged or Swept',
  appPriceXEma50Cross: 'appPriceXEma50Cross',
  appPriceXEma100Cross: 'appPriceXEma100Cross',
  appPriceXEma200Cross: 'appPriceXEma200Cross',
  appEma50XEma200Cross: 'appEma50XEma200Cross',
  mtfPriceXEma100Ema200Cross:   'mtfPriceXEma100Ema200Cross',
  mtfPriceXEma100Ema200TrendOnlyCross: 'mtfPriceXEma100Ema200TrendOnlyCross',

  ema12ema21SwingHighCloseSrCrossSignal: 'ema12ema21 SwingHigh CloseSrCross Signal',
  ema12ema21SwingLowCloseSrCrossSignal: 'ema12ema21 SwingLow CloseSrCross Signal',
  ema12ema21SwingHighWickSrCrossSignal: 'ema12ema21 SwingHigh WickSrCross Signal',
  ema12ema21SwingLowWickSrCrossSignal: 'ema12ema21 SwingLow WickSrCross Signal',
  ema12ema21SwingLevelStochRsiEntrySignal: 'ema12ema21 SwingLevel StochRsi Entry Signal',

  appPriceXEma12Cross: 'appPriceXEma12Cross',
  appPriceXEma12Ema21Cross: 'appPriceXEma12Ema21Cross',
  appEma12XEma21Cross: 'appEma12XEma21Cross',

  appPriceXEma21Cross: 'App Price X Ema21 Cross',
  appPriceXEma36Cross: 'App Price X Ema36 Cross',
  mtfPriceXEma21Ema36Cross: 'Mtf Price X Ema21Ema36 Cross',
  appEma21XEma36Cross: 'App Ema21 X Ema36 Cross',
  mtfPriceXEma21Ema36TrendOnlyCross: 'Mtf Price X Ema21Ema36 TrendOnly Cross',
  appEma21XEma36CrossEntrySRCrossSignal: 'App Ema21XEma36_Cross_Entry_SR Cross Signal',
  swingLevelStochRsiEntrySignal: 'Swing Level Stoch Rsi Entry Signal',
  appPriceXVwap24Cross: 'App Price X Vwap24 Cross',
  appPriceXVwapWeeklyCross: 'App Price X Vwap Weekly Cross',
  priceXEma36Rejection: 'Price X EMA36 Rejection/Bounce',
  candlePattern: 'Engulfing Candle Pattern',
  priceXSRTrendLineCross: 'Price X SR Trendline Cross',
  priceXChannelLineCross: 'Price X Channel Line Cross',
  priceXSupplyDemandZoneCross: 'Price X Supply Demand Zone Cross',
  priceXVWAP24Cross: 'Price X VWAP 24H Cross',
  priceXVWAPWeeklyCross: 'Price X VWAP Weekly Cross',
  priceXEma12Cross: 'Price X EMA12 Cross',
  priceXEma36Cross: 'Price X EMA36 Cross',
  ema12XEma36Cross: 'EMA12 X EMA36 Cross',
}[field] || field);

const CrossCombinationView = ({ data }) => {
  const { workspace } = useWorkspace();
  const safeData = ensureArray(data);
  const [globalCrossType, setGlobalCrossType] = useState('');
  const [globalSymbol, setGlobalSymbol] = useState('');

const filteredSymbols = useMemo(() => {
  return [...new Set(
    safeData
        .filter(item =>
          crossFields.some(field => {
            const v = item[field];
            return isActiveCrossValue(v, globalCrossType);
          })
        )
      .map(item => item.symbol)
  )];
}, [safeData, globalCrossType]);

  
  

  const clearGlobalFilters = () => {
    setGlobalCrossType('');
    setGlobalSymbol('');
  };


const globalFilteredRows = useMemo(() => {
    return safeData.filter(item => {
      const hasCross = crossFields.some(field => {
        const v = item[field];
        return isActiveCrossValue(v, globalCrossType);
      });
      return hasCross && (!globalSymbol || item.symbol === globalSymbol);
    });
}, [safeData, globalCrossType, globalSymbol]);

  
  
  return (
    <>
      <div className="cross-toolbar">
        <select
          id="globalCrossFilter"
          value={globalCrossType}
          onChange={(e) => setGlobalCrossType(e.target.value)}
        >
          <option value="">Select Cross Type</option>
          <option value="BULL">All BULL_CROSS</option>
          <option value="BEAR">All BEAR_CROSS</option>
        </select>

        <select
          id="globalSymbolFilter"
          value={globalSymbol}
          onChange={(e) => setGlobalSymbol(e.target.value)}
        >
          <option value="">Select Symbol</option>
          {filteredSymbols.map((sym) => (
            <option key={sym} value={sym}>
              {sym}
            </option>
          ))}
        </select>


              <button onClick={() => {
                const allFilteredSymbols = [...new Set(
                  globalFilteredRows.map(row => row.symbol).filter(Boolean)
                    .map(symbol => formatTradingViewSymbol(symbol, workspace))
                )];
                copyToClipboard(allFilteredSymbols.join(','));
              }}>
                Copy Symbols
              </button>


        <button className="clear-global-btn" onClick={clearGlobalFilters}>
          Clear Filters
        </button>
      </div>

      <div className="cross-container">
        {crossFields.map(field => {
          const fieldData = safeData.filter(item => {
            const v = item[field];
            return isActiveCrossValue(v, globalCrossType)
              && (!globalSymbol || item.symbol === globalSymbol);
          });
        if (!fieldData.length) return null;
        return <CrossSection key={field} field={field} data={fieldData} />;
      })}
      </div>
    </>
  );
};



const CrossSection = ({ field, data }) => {
  const { workspace } = useWorkspace();
  const safeData = ensureArray(data);
  const [showAll, setShowAll] = useState(false);
  // inside CrossSection
  const [sortConfig, setSortConfig] = useState({ key: 'change', direction: 'desc' });

  const [filters, setFilters] = useState({ symbol: '', timeFrame: '', crossType: '' });

  const symbols = [...new Set(safeData.map(d => d.symbol))];
  // inside CrossSection
  const timeFrames = [...new Set(safeData.map(d => d.timeFrame))].sort((a, b) => a - b);


const filtered = safeData
  .filter(d => !filters.symbol || d.symbol === filters.symbol)
  .filter(d => !filters.timeFrame || d.timeFrame === +filters.timeFrame)
  .filter(d => {
    if (!filters.crossType) return true;
    const v = d[field];
    return includesToken(v, filters.crossType); // ✅ case-insensitive contains
  });

  const sorted = [...filtered].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;

    let valA = a[key];
    let valB = b[key];

      // ✅ NEW: handle "Change %" numeric sorting properly
    if (key === 'change') {
      const numA = parseFloat(String(valA).replace('%', '')) || 0;
      const numB = parseFloat(String(valB).replace('%', '')) || 0;
      return direction === 'asc' ? numA - numB : numB - numA;
    }

    if (typeof valA === 'number' && typeof valB === 'number') {
      return direction === 'asc' ? valA - valB : valB - valA;
    }

  // In CrossSection, inside the sort comparator, replace the special-case block:
  if (key === field) {
    const a = norm(valA);
    const b = norm(valB);
    const rank = (s) => (s.includes('BEAR') ? 0 : s.includes('BULL') ? 1 : 2);
    const ra = rank(a);
    const rb = rank(b);
    return direction === 'asc' ? ra - rb : rb - ra;
  }


    return direction === 'asc'
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });

  const displayed = showAll ? sorted : sorted.slice(0, 5);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const getSignalSLValue = (item, field) => {
  const v = norm(item[field]);

  if (!v) return '';

  if (v.includes('SIGNAL_CLOSE')) return '';

  if (v.includes('BULL')) return item.bullishSignalSL || '';

  if (v.includes('BEAR')) return item.bearishSignalSL || '';

  return '';
};

  return (
    <div className="cross-section">
      <h3>{getSectionTitle(field)}</h3>
      <div className="filters">
        <select onChange={e => setFilters({ ...filters, symbol: e.target.value })} value={filters.symbol}>
          <option value="">Symbol</option>
          {symbols.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select onChange={e => setFilters({ ...filters, timeFrame: e.target.value })} value={filters.timeFrame}>
          <option value="">Time Frame</option>
          {timeFrames.map(t => <option key={t} value={t}>{t} min</option>)}
        </select>
        <select onChange={e => setFilters({ ...filters, crossType: e.target.value })} value={filters.crossType}>
          <option value="">Cross Type</option>
          <option value="BULL">BULL</option>
          <option value="BEAR">BEAR</option>
        </select>

        {filtered.length > 5 && (
          <button onClick={() => setShowAll(!showAll)} style={{ marginTop: '10px' }}>
            {showAll ? 'Collapse' : 'Expand'}
          </button>
        )}

        <button onClick={() => {
              const sectionSymbols = [...new Set(
                filtered.map(row => row.symbol).filter(Boolean)
                  .map(symbol => formatTradingViewSymbol(symbol, workspace))
              )];
              copyToClipboard(sectionSymbols.join(','));
        }}>
          Copy Symbols
        </button>

        <button onClick={() => setFilters({ symbol: '', timeFrame: '', crossType: '' })}>Clear Filters</button>
      </div>

      <div className="cross-table-wrapper">
        <table className="cross-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => handleSort('symbol')}>Symbol {sortConfig.key === 'symbol' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⬍'}</th>
              <th>Chart</th>
              <th onClick={() => handleSort('change')}>Change % {sortConfig.key === 'change' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⬍'}</th>
              <th onClick={() => handleSort('marketPrice')}>Market Price {sortConfig.key === 'marketPrice' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⬍'}</th>
              <th onClick={() => handleSort('timeFrame')}>Time Frame {sortConfig.key === 'timeFrame' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⬍'}</th>
              <th onClick={() => handleSort('volumeRank')}>VolumeRank {sortConfig.key === 'volumeRank' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⬍'}</th>
              <th>Signal SL</th>              
              <th onClick={() => handleSort(field)}>{field} {sortConfig.key === field ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⬍'}</th>

              {field === "priceXLowerEmaStackEdgeCross" && (
                  <>
                    <th onClick={() => handleSort('lowerEmaStackEdgeCrossSignalStopLoss')}>
                      LES SL {sortConfig.key === 'lowerEmaStackEdgeCrossSignalStopLoss' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⬍'}
                    </th>
                    <th onClick={() => handleSort('lowerEmaStackEdgeCrossSignalStopLossDistance')}>
                      LES SL Dist % {sortConfig.key === 'lowerEmaStackEdgeCrossSignalStopLossDistance' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⬍'}
                    </th>
                  </>
                )}

            </tr>
          </thead>
          <tbody>
            {displayed.map((item, idx) => (
              <tr key={idx} className={getCrossClass(item[field])}>
                <td>{idx + 1}</td>
                <td>{item.symbol}</td>
                <td>
                  <TradingViewChartIcon
                    symbol={item.symbol}
                    timeFrame={item.timeFrame}
                    exchange={item.exchange}
                  />
                </td>
                <td>{item.change}</td>
                <td>{item.marketPrice}</td>
                <td>{item.timeFrame} min</td>
                <td>{item.volumeRank}</td>   
                <td>{getSignalSLValue(item, field)}</td> 
                <td>{item[field]}</td>       

                {field === "priceXLowerEmaStackEdgeCross" && (
                  <>
                    <td>{item.lowerEmaStackEdgeCrossSignalStopLoss}</td>
                    <td>{item.lowerEmaStackEdgeCrossSignalStopLossDistance}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CrossCombinationView;
